import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import client from "@/api/client";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading"); // loading | success | failed
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await client.get(`/payments/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("success");
        } else if (res.data.status === "expired") {
          setStatus("failed");
        } else if (tries < 8) {
          tries++;
          setAttempts(tries);
          setTimeout(poll, 2500);
        } else {
          setStatus("failed");
        }
      } catch {
        setStatus("failed");
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <Loader2 size={48} className="text-[#006080] mx-auto mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying payment...</h2>
            <p className="text-gray-500 text-sm">Please wait while we confirm your payment.</p>
            {attempts > 0 && <p className="text-xs text-gray-400 mt-2">Checking... ({attempts}/8)</p>}
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">Thank you for your purchase. Your account will be activated shortly and you'll receive a confirmation email.</p>
            <Button onClick={() => navigate("/login")} className="bg-[#006080] hover:bg-[#004d66] text-white w-full">Go to Login</Button>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle size={56} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-black text-gray-900 mb-2">Payment Not Confirmed</h2>
            <p className="text-gray-600 mb-6">We couldn't confirm your payment. If you completed payment, please contact our admin team with your session ID.</p>
            {sessionId && <p className="text-xs font-mono bg-gray-100 p-2 rounded mb-4 break-all">{sessionId}</p>}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/")} className="flex-1">Back to Home</Button>
              <Button onClick={() => navigate("/login")} className="bg-[#006080] hover:bg-[#004d66] text-white flex-1">Try Login</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
